import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  Target, 
  Award, 
  BarChart3, 
  LineChart, 
  Activity, 
  Zap, 
  BookOpen, 
  Star,
  Calendar,
  Filter,
  Download,
  RefreshCw,
  Eye,
  ChevronRight,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Trophy,
  Flame,
  Brain,
  Timer,
  Gauge
} from 'lucide-react';

// Interfaces
interface PerformanceData {
  id: string;
  date: string;
  examName: string;
  type: 'LGS' | 'Deneme';
  totalScore: number;
  subjects: {
    [key: string]: {
      correct: number;
      wrong: number;
      empty: number;
      net: number;
      score: number;
      percentage: number;
    };
  };
  timeSpent: number; // dakika
  ranking?: number;
  totalParticipants?: number;
}

interface AnalysisMetrics {
  overallTrend: 'up' | 'down' | 'stable';
  trendPercentage: number;
  strongestSubject: string;
  weakestSubject: string;
  consistencyScore: number;
  improvementRate: number;
  averageStudyTime: number;
  totalExams: number;
  bestPerformance: PerformanceData | null;
  recentPerformance: PerformanceData[];
}

const SUBJECTS = ['Türkçe', 'Matematik', 'Fen Bilimleri', 'T.C. İnkılap Tarihi', 'İngilizce', 'Din Kültürü'];

const SUBJECT_COLORS = {
  'Türkçe': 'from-red-500 to-pink-500',
  'Matematik': 'from-blue-500 to-cyan-500',
  'Fen Bilimleri': 'from-green-500 to-emerald-500',
  'T.C. İnkılap Tarihi': 'from-purple-500 to-violet-500',
  'İngilizce': 'from-yellow-500 to-orange-500',
  'Din Kültürü': 'from-indigo-500 to-blue-500'
};

const SUBJECT_ICONS = {
  'Türkçe': BookOpen,
  'Matematik': BarChart3,
  'Fen Bilimleri': Zap,
  'T.C. İnkılap Tarihi': Award,
  'İngilizce': Star,
  'Din Kültürü': Target
};

const PerformanceAnalysis: React.FC = () => {
  const [performanceData, setPerformanceData] = useState<PerformanceData[]>([]);
  const [metrics, setMetrics] = useState<AnalysisMetrics | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<'7d' | '30d' | '90d' | 'all'>('30d');
  const [selectedSubject, setSelectedSubject] = useState<string>('Tümü');
  const [activeView, setActiveView] = useState<'overview' | 'trends' | 'subjects' | 'insights'>('overview');

  // Demo data
  useEffect(() => {
    const demoData: PerformanceData[] = [
      {
        id: '1',
        date: '2024-01-20',
        examName: 'Karekök LGS Deneme 15',
        type: 'Deneme',
        totalScore: 357.5,
        subjects: {
          'Türkçe': { correct: 17, wrong: 2, empty: 1, net: 16.33, score: 71.06, percentage: 85 },
          'Matematik': { correct: 15, wrong: 3, empty: 2, net: 14, score: 59.56, percentage: 75 },
          'Fen Bilimleri': { correct: 15, wrong: 3, empty: 2, net: 14, score: 57.72, percentage: 75 },
          'T.C. İnkılap Tarihi': { correct: 9, wrong: 1, empty: 0, net: 8.67, score: 14.45, percentage: 90 },
          'İngilizce': { correct: 8, wrong: 1, empty: 1, net: 7.67, score: 11.57, percentage: 80 },
          'Din Kültürü': { correct: 9, wrong: 1, empty: 0, net: 8.67, score: 16.46, percentage: 90 }
        },
        timeSpent: 240,
        ranking: 1250,
        totalParticipants: 15000
      },
      {
        id: '2',
        date: '2024-01-15',
        examName: 'Biltest LGS Deneme 8',
        type: 'Deneme',
        totalScore: 345.2,
        subjects: {
          'Türkçe': { correct: 15, wrong: 3, empty: 2, net: 14, score: 60.89, percentage: 75 },
          'Matematik': { correct: 14, wrong: 4, empty: 2, net: 12.67, score: 53.88, percentage: 70 },
          'Fen Bilimleri': { correct: 14, wrong: 4, empty: 2, net: 12.67, score: 52.24, percentage: 70 },
          'T.C. İnkılap Tarihi': { correct: 8, wrong: 2, empty: 0, net: 7.33, score: 12.22, percentage: 80 },
          'İngilizce': { correct: 6, wrong: 3, empty: 1, net: 5, score: 7.54, percentage: 60 },
          'Din Kültürü': { correct: 8, wrong: 2, empty: 0, net: 7.33, score: 13.92, percentage: 80 }
        },
        timeSpent: 235,
        ranking: 1800,
        totalParticipants: 12000
      },
      {
        id: '3',
        date: '2024-01-10',
        examName: 'Esen LGS Deneme 12',
        type: 'Deneme',
        totalScore: 332.8,
        subjects: {
          'Türkçe': { correct: 14, wrong: 4, empty: 2, net: 12.67, score: 55.11, percentage: 70 },
          'Matematik': { correct: 13, wrong: 5, empty: 2, net: 11.33, score: 48.20, percentage: 65 },
          'Fen Bilimleri': { correct: 13, wrong: 5, empty: 2, net: 11.33, score: 46.71, percentage: 65 },
          'T.C. İnkılap Tarihi': { correct: 7, wrong: 2, empty: 1, net: 6.33, score: 10.56, percentage: 70 },
          'İngilizce': { correct: 5, wrong: 4, empty: 1, net: 3.67, score: 5.53, percentage: 50 },
          'Din Kültürü': { correct: 7, wrong: 2, empty: 1, net: 6.33, score: 12.02, percentage: 70 }
        },
        timeSpent: 245,
        ranking: 2200,
        totalParticipants: 10000
      }
    ];

    setPerformanceData(demoData);
    calculateMetrics(demoData);
  }, []);

  const calculateMetrics = (data: PerformanceData[]) => {
    if (data.length === 0) return;

    const sortedData = data.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const recentData = sortedData.slice(-5);
    
    // Trend hesaplama
    const firstScore = sortedData[0].totalScore;
    const lastScore = sortedData[sortedData.length - 1].totalScore;
    const trendPercentage = ((lastScore - firstScore) / firstScore) * 100;
    
    // En güçlü ve zayıf ders
    const subjectAverages: { [key: string]: number } = {};
    SUBJECTS.forEach(subject => {
      const scores = data.map(d => d.subjects[subject]?.percentage || 0);
      subjectAverages[subject] = scores.reduce((sum, score) => sum + score, 0) / scores.length;
    });
    
    const strongestSubject = Object.keys(subjectAverages).reduce((a, b) => 
      subjectAverages[a] > subjectAverages[b] ? a : b
    );
    
    const weakestSubject = Object.keys(subjectAverages).reduce((a, b) => 
      subjectAverages[a] < subjectAverages[b] ? a : b
    );

    // Tutarlılık skoru (standart sapma ile)
    const scores = data.map(d => d.totalScore);
    const average = scores.reduce((sum, score) => sum + score, 0) / scores.length;
    const variance = scores.reduce((sum, score) => sum + Math.pow(score - average, 2), 0) / scores.length;
    const consistencyScore = Math.max(0, 100 - Math.sqrt(variance) / 2);

    setMetrics({
      overallTrend: trendPercentage > 2 ? 'up' : trendPercentage < -2 ? 'down' : 'stable',
      trendPercentage: Math.abs(trendPercentage),
      strongestSubject,
      weakestSubject,
      consistencyScore,
      improvementRate: trendPercentage,
      averageStudyTime: data.reduce((sum, d) => sum + d.timeSpent, 0) / data.length,
      totalExams: data.length,
      bestPerformance: data.reduce((best, current) => 
        current.totalScore > (best?.totalScore || 0) ? current : best, null
      ),
      recentPerformance: recentData
    });
  };

  const getPerformanceLevel = (score: number): { level: string; color: string; icon: React.ElementType } => {
    if (score >= 450) return { level: 'Mükemmel', color: 'text-green-600', icon: Trophy };
    if (score >= 400) return { level: 'Çok İyi', color: 'text-blue-600', icon: Award };
    if (score >= 350) return { level: 'İyi', color: 'text-yellow-600', icon: Star };
    if (score >= 300) return { level: 'Orta', color: 'text-orange-600', icon: Target };
    return { level: 'Geliştirilmeli', color: 'text-red-600', icon: AlertTriangle };
  };

  const renderOverview = () => (
    <div className="space-y-8">
      {/* Performance Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Overall Score */}
        <div className="relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-cyan-500/10"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                <Gauge className="text-white" size={24} />
              </div>
              {metrics?.overallTrend === 'up' ? (
                <TrendingUp className="text-green-500" size={20} />
              ) : metrics?.overallTrend === 'down' ? (
                <TrendingDown className="text-red-500" size={20} />
              ) : (
                <Activity className="text-gray-500" size={20} />
              )}
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {performanceData.length > 0 ? performanceData[performanceData.length - 1].totalScore.toFixed(0) : '0'}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Son Sınav Puanı
            </div>
            {metrics && (
              <div className={`text-xs mt-2 ${metrics.overallTrend === 'up' ? 'text-green-600' : metrics.overallTrend === 'down' ? 'text-red-600' : 'text-gray-600'}`}>
                {metrics.overallTrend === 'up' ? '↗' : metrics.overallTrend === 'down' ? '↘' : '→'} %{metrics.trendPercentage.toFixed(1)}
              </div>
            )}
          </div>
        </div>

        {/* Consistency Score */}
        <div className="relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-violet-500/10"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-500 rounded-xl flex items-center justify-center">
                <Target className="text-white" size={24} />
              </div>
              <CheckCircle className="text-purple-500" size={20} />
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {metrics?.consistencyScore.toFixed(0) || '0'}%
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Tutarlılık Skoru
            </div>
          </div>
        </div>

        {/* Total Exams */}
        <div className="relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-emerald-500/10"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                <BarChart3 className="text-white" size={24} />
              </div>
              <Users className="text-green-500" size={20} />
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {metrics?.totalExams || 0}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Toplam Sınav
            </div>
          </div>
        </div>

        {/* Average Study Time */}
        <div className="relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-red-500/10"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                <Clock className="text-white" size={24} />
              </div>
              <Timer className="text-orange-500" size={20} />
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {metrics?.averageStudyTime.toFixed(0) || '0'}dk
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Ortalama Süre
            </div>
          </div>
        </div>
      </div>

      {/* Performance Level & Best Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Current Performance Level */}
        <div className="relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-500/5"></div>
          <div className="relative z-10">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Mevcut Performans Seviyesi</h3>
            {performanceData.length > 0 && (() => {
              const latestScore = performanceData[performanceData.length - 1].totalScore;
              const { level, color, icon: Icon } = getPerformanceLevel(latestScore);
              return (
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
                    <Icon className="text-white" size={32} />
                  </div>
                  <div className={`text-2xl font-bold mb-2 ${color}`}>
                    {level}
                  </div>
                  <div className="text-gray-600 dark:text-gray-400 mb-4">
                    {latestScore.toFixed(0)} / 500 puan
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div 
                      className="bg-gradient-to-r from-indigo-500 to-purple-500 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${(latestScore / 500) * 100}%` }}
                    ></div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Best Performance */}
        <div className="relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20">
          <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/5 to-orange-500/5"></div>
          <div className="relative z-10">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">En İyi Performans</h3>
            {metrics?.bestPerformance && (
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center">
                    <Trophy className="text-white" size={24} />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {metrics.bestPerformance.totalScore.toFixed(0)}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {metrics.bestPerformance.examName}
                    </div>
                  </div>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  📅 {new Date(metrics.bestPerformance.date).toLocaleDateString('tr-TR')}
                </div>
                {metrics.bestPerformance.ranking && (
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    🏆 {metrics.bestPerformance.ranking}. sıra / {metrics.bestPerformance.totalParticipants} kişi
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Subject Strengths & Weaknesses */}
      <div className="relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-teal-500/5"></div>
        <div className="relative z-10">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Güçlü & Zayıf Yönler</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Strongest Subject */}
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                <TrendingUp className="text-white" size={24} />
              </div>
              <div className="text-lg font-bold text-green-600 mb-2">En Güçlü Ders</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {metrics?.strongestSubject}
              </div>
            </div>

            {/* Weakest Subject */}
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-red-500 to-pink-500 rounded-xl flex items-center justify-center">
                <TrendingDown className="text-white" size={24} />
              </div>
              <div className="text-lg font-bold text-red-600 mb-2">Geliştirilmesi Gereken</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {metrics?.weakestSubject}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTrends = () => (
    <div className="space-y-8">
      {/* Score Trend Chart */}
      <div className="relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-500/5"></div>
        <div className="relative z-10">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Puan Trendi</h3>
          <div className="h-80 bg-white/50 dark:bg-gray-600/50 rounded-xl p-4">
            <svg className="w-full h-full" viewBox="0 0 800 300">
              <defs>
                <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.1" />
                </linearGradient>
              </defs>
              
              {/* Grid lines */}
              {[0, 100, 200, 300, 400, 500].map((value) => (
                <line 
                  key={value}
                  x1="60" 
                  y1={280 - (value * 0.5)} 
                  x2="740" 
                  y2={280 - (value * 0.5)} 
                  stroke="currentColor" 
                  strokeWidth="1" 
                  className="text-gray-200 dark:text-gray-600"
                  opacity="0.3"
                />
              ))}
              
              {/* Y-axis labels */}
              {[0, 100, 200, 300, 400, 500].map((value) => (
                <text 
                  key={value}
                  x="50" 
                  y={285 - (value * 0.5)} 
                  textAnchor="end" 
                  className="text-xs fill-gray-600 dark:fill-gray-400"
                >
                  {value}
                </text>
              ))}
              
              {/* Area under curve */}
              {performanceData.length > 1 && (
                <polygon
                  fill="url(#scoreGradient)"
                  points={`60,280 ${performanceData
                    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                    .map((data, index) => {
                      const x = 60 + (index * (680 / (performanceData.length - 1)));
                      const y = 280 - (data.totalScore * 0.5);
                      return `${x},${y}`;
                    })
                    .join(' ')} 740,280`}
                />
              )}
              
              {/* Main line */}
              {performanceData.length > 1 && (
                <polyline
                  fill="none"
                  stroke="#3B82F6"
                  strokeWidth="3"
                  points={performanceData
                    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                    .map((data, index) => {
                      const x = 60 + (index * (680 / (performanceData.length - 1)));
                      const y = 280 - (data.totalScore * 0.5);
                      return `${x},${y}`;
                    })
                    .join(' ')
                  }
                />
              )}
              
              {/* Data points */}
              {performanceData
                .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                .map((data, index) => {
                  const x = 60 + (index * (680 / Math.max(1, performanceData.length - 1)));
                  const y = 280 - (data.totalScore * 0.5);
                  return (
                    <g key={data.id}>
                      <circle
                        cx={x}
                        cy={y}
                        r="6"
                        fill="white"
                        stroke="#3B82F6"
                        strokeWidth="3"
                      />
                      <text
                        x={x}
                        y={y - 15}
                        textAnchor="middle"
                        className="text-xs font-bold fill-gray-700 dark:fill-gray-300"
                      >
                        {data.totalScore.toFixed(0)}
                      </text>
                      <text
                        x={x}
                        y={300}
                        textAnchor="middle"
                        className="text-xs fill-gray-600 dark:fill-gray-400"
                      >
                        {new Date(data.date).toLocaleDateString('tr-TR', { month: 'short', day: 'numeric' })}
                      </text>
                    </g>
                  );
                })}
            </svg>
          </div>
        </div>
      </div>

      {/* Recent Performance */}
      <div className="relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5"></div>
        <div className="relative z-10">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Son Performanslar</h3>
          <div className="space-y-4">
            {performanceData
              .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
              .slice(0, 5)
              .map((data, index) => (
                <div key={data.id} className="flex items-center justify-between p-4 bg-white/50 dark:bg-gray-700/50 rounded-xl">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      index === 0 ? 'bg-gradient-to-br from-yellow-500 to-orange-500' :
                      index === 1 ? 'bg-gradient-to-br from-gray-400 to-gray-500' :
                      index === 2 ? 'bg-gradient-to-br from-orange-600 to-red-600' :
                      'bg-gradient-to-br from-blue-500 to-indigo-500'
                    }`}>
                      <span className="text-white font-bold text-sm">{index + 1}</span>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {data.examName}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {new Date(data.date).toLocaleDateString('tr-TR')}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-gray-900 dark:text-white">
                      {data.totalScore.toFixed(0)}
                    </div>
                    {data.ranking && (
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {data.ranking}. sıra
                      </div>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSubjects = () => (
    <div className="space-y-8">
      {/* Subject Performance Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {SUBJECTS.map((subject) => {
          const SubjectIcon = SUBJECT_ICONS[subject as keyof typeof SUBJECT_ICONS];
          const subjectData = performanceData.map(d => d.subjects[subject]);
          const avgPercentage = subjectData.reduce((sum, data) => sum + (data?.percentage || 0), 0) / subjectData.length;
          const avgNet = subjectData.reduce((sum, data) => sum + (data?.net || 0), 0) / subjectData.length;
          
          return (
            <div key={subject} className="relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20">
              <div className={`absolute inset-0 bg-gradient-to-br ${SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS]} opacity-5`}></div>
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 bg-gradient-to-br ${SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS]} rounded-xl flex items-center justify-center`}>
                    <SubjectIcon className="text-white" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 dark:text-white">{subject}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Ortalama: %{avgPercentage.toFixed(0)}</p>
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600 dark:text-gray-400">Başarı Oranı</span>
                    <span className="font-semibold text-gray-900 dark:text-white">%{avgPercentage.toFixed(0)}</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className={`bg-gradient-to-r ${SUBJECT_COLORS[subject as keyof typeof SUBJECT_COLORS]} h-2 rounded-full transition-all duration-500`}
                      style={{ width: `${avgPercentage}%` }}
                    ></div>
                  </div>
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-lg font-bold text-gray-900 dark:text-white">
                      {avgNet.toFixed(1)}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      Ortalama Net
                    </div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-gray-900 dark:text-white">
                      {subjectData[subjectData.length - 1]?.net.toFixed(1) || '0.0'}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      Son Net
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderInsights = () => (
    <div className="space-y-8">
      {/* AI Insights */}
      <div className="relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-500/5"></div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center">
              <Brain className="text-white" size={24} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Akıllı Analiz & Öneriler</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Performance Insights */}
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3">📊 Performans Analizi</h4>
              <div className="space-y-3">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                  <div className="flex items-start gap-3">
                    <TrendingUp className="text-blue-600 mt-1" size={16} />
                    <div>
                      <div className="font-medium text-blue-900 dark:text-blue-100">Genel Trend</div>
                      <div className="text-sm text-blue-700 dark:text-blue-300">
                        {metrics?.overallTrend === 'up' ? 'Performansınız yükseliş trendinde! Devam edin.' :
                         metrics?.overallTrend === 'down' ? 'Performansınızda düşüş var. Çalışma stratejinizi gözden geçirin.' :
                         'Performansınız stabil. Daha tutarlı sonuçlar için çalışın.'}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
                  <div className="flex items-start gap-3">
                    <Target className="text-green-600 mt-1" size={16} />
                    <div>
                      <div className="font-medium text-green-900 dark:text-green-100">Tutarlılık</div>
                      <div className="text-sm text-green-700 dark:text-green-300">
                        {(metrics?.consistencyScore || 0) > 80 ? 'Çok tutarlı performans gösteriyorsunuz!' :
                         (metrics?.consistencyScore || 0) > 60 ? 'Tutarlılığınızı artırabilirsiniz.' :
                         'Performansınızda dalgalanmalar var. Düzenli çalışma önemli.'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Study Recommendations */}
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3">💡 Çalışma Önerileri</h4>
              <div className="space-y-3">
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800">
                  <div className="flex items-start gap-3">
                    <Flame className="text-yellow-600 mt-1" size={16} />
                    <div>
                      <div className="font-medium text-yellow-900 dark:text-yellow-100">Öncelikli Ders</div>
                      <div className="text-sm text-yellow-700 dark:text-yellow-300">
                        {metrics?.weakestSubject} dersine daha fazla zaman ayırın. Bu derste %20 artış hedefleyin.
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200 dark:border-purple-800">
                  <div className="flex items-start gap-3">
                    <Clock className="text-purple-600 mt-1" size={16} />
                    <div>
                      <div className="font-medium text-purple-900 dark:text-purple-100">Zaman Yönetimi</div>
                      <div className="text-sm text-purple-700 dark:text-purple-300">
                        Ortalama sınav süreniz {metrics?.averageStudyTime.toFixed(0)} dakika. 
                        {(metrics?.averageStudyTime || 0) > 240 ? ' Hızınızı artırabilirsiniz.' : ' İyi bir tempoda ilerliyorsunuz.'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Goal Setting */}
      <div className="relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-teal-500/5"></div>
        <div className="relative z-10">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">🎯 Hedef Belirleme</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-white/50 dark:bg-gray-700/50 rounded-xl">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                <Target className="text-white" size={24} />
              </div>
              <div className="text-lg font-bold text-gray-900 dark:text-white mb-2">Kısa Vadeli</div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">Sonraki sınavda</div>
              <div className="text-2xl font-bold text-blue-600">+15 puan</div>
            </div>
            
            <div className="text-center p-6 bg-white/50 dark:bg-gray-700/50 rounded-xl">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-purple-500 to-violet-500 rounded-xl flex items-center justify-center">
                <Award className="text-white" size={24} />
              </div>
              <div className="text-lg font-bold text-gray-900 dark:text-white mb-2">Orta Vadeli</div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">Bu ay sonunda</div>
              <div className="text-2xl font-bold text-purple-600">400+ puan</div>
            </div>
            
            <div className="text-center p-6 bg-white/50 dark:bg-gray-700/50 rounded-xl">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                <Trophy className="text-white" size={24} />
              </div>
              <div className="text-lg font-bold text-gray-900 dark:text-white mb-2">Uzun Vadeli</div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">LGS hedefi</div>
              <div className="text-2xl font-bold text-green-600">450+ puan</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl p-8 mb-8 shadow-xl border border-white/20">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 via-purple-500/10 to-pink-500/10"></div>
          <div className="relative z-10">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                  Performans Analizi
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  Detaylı performans metrikleri ve gelişim önerileri
                </p>
              </div>
              
              <div className="flex items-center gap-4">
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value as any)}
                  className="px-4 py-2 bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm border border-white/30 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 text-gray-900 dark:text-white"
                >
                  <option value="7d">Son 7 Gün</option>
                  <option value="30d">Son 30 Gün</option>
                  <option value="90d">Son 90 Gün</option>
                  <option value="all">Tüm Zamanlar</option>
                </select>
                
                <button className="p-3 bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm rounded-xl hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all duration-300 border border-white/30">
                  <RefreshCw className="text-indigo-600" size={20} />
                </button>
                
                <button className="p-3 bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm rounded-xl hover:bg-green-50 dark:hover:bg-green-900/20 transition-all duration-300 border border-white/30">
                  <Download className="text-green-600" size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-2 mb-8 shadow-xl border border-white/20">
          <div className="flex gap-2">
            {[
              { id: 'overview', label: 'Genel Bakış', icon: Activity },
              { id: 'trends', label: 'Trendler', icon: LineChart },
              { id: 'subjects', label: 'Dersler', icon: BookOpen },
              { id: 'insights', label: 'Öngörüler', icon: Brain }
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveView(id as any)}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                  activeView === id
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-gray-700/50'
                }`}
              >
                <Icon size={18} />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        {activeView === 'overview' && renderOverview()}
        {activeView === 'trends' && renderTrends()}
        {activeView === 'subjects' && renderSubjects()}
        {activeView === 'insights' && renderInsights()}
      </div>
    </div>
  );
};

export default PerformanceAnalysis;